import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import videojs from 'video.js';
import '@fupa/fupa-videojs-playlist';
import styled from 'styled-components';
import CountdownTimer from 'app/components/tv/CountdownTimer';
import { CustomControls } from 'app/components/tv/customControls/CustomControls';
import { PlaylistOverlay } from 'app/components/tv/PlaylistOverlay';
import { HeaderBar } from 'app/components/tv/customControls/HeaderBar';
import { PlayerOptions } from 'app/components/tv/customControls/PlayerOptions';
import { Slide } from '@mui/material';
import { getUserAgentData } from 'app/helpers/helpers';
import { getFullScreenElement, useFullscreen } from 'app/hooks/useFullscreen';
import { Onboarding } from 'app/components/tv/Onboarding';
import { useMute } from 'app/components/tv/helpers/useMute';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { LoadingOverlay } from 'app/components/tv/LoadingOverlay';
import { FinishedPlaylistOverlay } from 'app/components/tv/FinishedPlaylistOverlay';
import { trackVideoProgress } from 'app/components/tv/helpers/trackVideoProgress';
import FuPaTvLogo from 'app/components/tv/FuPaTvLogo';
import { ErrorOverlay } from 'app/components/tv/ErrorOverlay';
import { VideoEvent } from 'app/components/tv/events/VideoEvent';
import VideoEventProvider from 'app/components/tv/events/VideoEventProvider';
import VideoJsPlayerFactory from 'app/components/tv/VideoJsPlayerFactory';
import ChangedContentEvent from 'app/components/tv/events/ChangedContentEvent';
import VideoAdController from 'app/components/tv/VideoAdController';
import VideoAds from 'app/components/tv/handler/VideoAds';
import LocalStorageManager from 'app/services/web-storage/LocalStorageManager';
import { reduxSelectCurrentUser } from 'app/hooks/reduxSelectCurrentUser';
import VideoCreator from 'app/components/tv/models/VideoCreator';
const StyledVJS = styled.div.withConfig({
  componentId: "sc-1jaonza-0"
})(["width:100%;aspect-ratio:16/9;background-color:black;visibility:hidden;"]);
export default function VideoJS(_ref) {
  var _LocalStorageManager$;
  let {
    onReady,
    video,
    playlist,
    setCurrentVideo,
    playerReady,
    isNetworkError,
    setIsNetworkError,
    isFirstPage
  } = _ref;
  const videoRef = useRef(null);
  const videoContainer = useRef(null);
  const playerRef = useRef(null);
  const [cssLoaded, setCssLoaded] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const [adPlaying, setAdPlaying] = useState(false);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [hideAll, setHideAll] = useState(false);
  const [playlistFinished, setPlaylistFinished] = useState(false);
  const history = useHistory();
  const disableVideoAds = (_LocalStorageManager$ = LocalStorageManager.getInstance().getItem('disableVideoAds')) !== null && _LocalStorageManager$ !== void 0 ? _LocalStorageManager$ : false;
  const {
    ppid
  } = useSelector(reduxSelectCurrentUser);
  const playlistPluginRef = useRef(null);
  const playlistRef = useRef(null);
  const currentVideoIndex = useMemo(() => {
    return playlist.indexOf(video);
  }, [playlist, video]);
  const {
    deviceType
  } = getUserAgentData();
  const isMobile = deviceType === 'mobile' || deviceType === 'tablet';
  const {
    exitFullscreen,
    requestFullscreen
  } = useFullscreen();
  const {
    muted,
    forceMuted,
    setMuted,
    toggleMute
  } = useMute(video);
  useEffect(() => {
    Promise.all([
    // @ts-ignore
    import('video.js/dist/video-js.css'),
    // @ts-ignore
    import('./videojs.custom.css'),
    // @ts-ignore
    import('videojs-ima/dist/videojs.ima.css')]).then(() => setCssLoaded(true));
  }, []);
  useEffect(() => {
    const player = playerRef.current;
    if (!player) {
      return;
    }
    const videoElement = player.el().querySelector('video');
    if (!videoElement) {
      return;
    }
    const poster = player.getChild('posterImage');
    if (playlistOpen) {
      videoElement.classList.add('blur');
      if (poster) {
        poster.addClass('blur');
      }
    } else {
      videoElement.classList.remove('blur');
      if (poster) {
        poster.removeClass('blur');
      }
    }
  }, [playerRef, playlistOpen]);
  useEffect(() => {
    // update plugin-playlist when new video is added to playlist by websocket
    if (playlistRef.current) {
      const videoNumberChanged = playlistRef.current.get().length !== playlist.length();
      if (videoNumberChanged) {
        const newVideo = playlist.getLastVideo();
        playlistRef.current.add(newVideo);
      }
    }
  }, [playlist]);
  const handlePlayerReady = player => {
    playerRef.current = player;
    const createVideoPlayed25Trigger = () => {
      const currentVideo = playlistRef.current.getCurrentItem();
      const checkPlayedTime = () => {
        var _currentVideo$videoDu;
        const currentTime = player.currentTime() - currentVideo.startTime;
        const targetTime = ((_currentVideo$videoDu = currentVideo.videoDuration) !== null && _currentVideo$videoDu !== void 0 ? _currentVideo$videoDu : 0) * 0.25;
        if (currentTime >= targetTime) {
          player.off('timeupdate', checkPlayedTime);
          player.trigger(VideoEvent.VideoPlayed25);
        }
      };
      // remove existing listener to avoid multiple listeners
      player.off('timeupdate', checkPlayedTime);
      player.on('timeupdate', checkPlayedTime);
    };
    player.on('playlistitem', () => {
      createVideoPlayed25Trigger();
      const currentItem = playlistRef.current.getCurrentItem();
      const currItem = VideoCreator.createVideo(currentItem);
      setCurrentVideo(currItem);
    });
    player.on('playlistended', () => {
      setPlaylistFinished(true);
    });
    player.on('timeupdate', () => {
      const currentVideo = playlistRef.current.getCurrentItem();
      const currVideo = VideoCreator.createVideo(currentVideo);
      const endTime = currVideo.manuallyEndAt();
      if (endTime && player.currentTime() >= endTime) {
        trackVideoProgress(currVideo, player.played());
        player.pause();
        playlistPluginRef.current.loadNextItem();
      }
    });
    onReady && onReady(player);
  };
  useEffect(() => {
    const videoEventProvider = VideoEventProvider.getInstance();
    videoEventProvider.up();
    const initializePlayer = async () => {
      const videoAdController = VideoAdController.getInstance(disableVideoAds, player => new VideoAds(player));
      await videoAdController.loadImaScript();
      const player = VideoJsPlayerFactory.create(() => handlePlayerReady(player));
      player.adsDisabled = disableVideoAds;
      initializePluginPlaylist(player);
      videoAdController.setupAds(player, setAdPlaying, setRemainingTime, isFirstPage, ppid);
    };
    initializePlayer();
    return () => {
      videoEventProvider.down();
      VideoAdController.getInstance().dispose();
      VideoJsPlayerFactory.destroy();
    };
  }, []);
  const createPluginPlaylist = playlist => {
    const playlistPluginClass = videojs.getPlugin('playlistPlugin');
    const videoList = playlist.getVideos();
    const newPlaylist = playlistPluginClass.createPlaylistFrom(videoList);
    playlistRef.current = newPlaylist;
    return newPlaylist;
  };
  const setFirstVideoScene = playlistPlugin => {
    var _playlist$indexOf;
    const videoIndex = (_playlist$indexOf = playlist.indexOf(video)) !== null && _playlist$indexOf !== void 0 ? _playlist$indexOf : 0;
    playlistPlugin.loadPlaylistItem(videoIndex);
  };
  const loadSource = useCallback(playlistPlugin => {
    playlistPlugin.loadSource_ = item => {
      return VideoEventProvider.getInstance().dispatch(new ChangedContentEvent(playlist.findBySlug(item.slug)));
    };
  }, [playlist]);
  const initializePluginPlaylist = useCallback(player => {
    const playlistPlugin = playlistPluginRef.current = player.playlistPlugin();
    const newPlaylist = createPluginPlaylist(playlist);
    playlistPlugin.loadPlaylist(newPlaylist);
    playlistPlugin.setAutoadvanceDelay(0);
    setFirstVideoScene(playlistPlugin);
    loadSource(playlistPlugin);
  }, [playlist]);
  useEffect(() => {
    const playlistPlugin = playlistPluginRef.current;
    if (!playlistPlugin) {
      return;
    }
    loadSource(playlistPlugin);
  }, [playlist]);
  useEffect(() => {
    if ('orientation' in screen && isMobile) {
      screen.orientation.onchange = () => {
        const orientation = screen.orientation.type;
        const fullScreenElement = getFullScreenElement(document);
        if (orientation.startsWith('landscape') && !fullScreenElement) {
          const elem = document.documentElement;
          requestFullscreen(elem);
        } else if (orientation.startsWith('portrait') && fullScreenElement) {
          exitFullscreen();
        }
      };
    }
  }, [isMobile]);

  // mute/unmute player on state change
  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      if (forceMuted) {
        player.muted(true);
      } else {
        player.muted(muted);
      }
    }
  }, [muted, forceMuted, playerReady]);

  // handle mute state on start and end of ad
  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      if (adPlaying) {
        if (forceMuted && !muted) {
          // Play ad unmuted if current video is forced muted and player has not been muted
          try {
            player.ima.controller.onAdMuteClick();
          } catch (e) {}
        }
        player.muted(muted);
      } else {
        if (forceMuted) {
          player.muted(true);
        } else {
          setMuted(player.muted());
        }
      }
    }
  }, [adPlaying, muted]);
  const handleExit = async () => {
    if (getFullScreenElement(document)) {
      exitFullscreen();
    }
    setIsNetworkError(false);
    if (isFirstPage) {
      var _video$getMatchLink;
      history.push((_video$getMatchLink = video.getMatchLink()) !== null && _video$getMatchLink !== void 0 ? _video$getMatchLink : '/start');
    } else {
      history.goBack();
    }
  };
  const handlePlaylistRestart = () => {
    const playlistController = playlistPluginRef.current;
    playlistController.loadFirstItem();
    setCurrentVideo(playlist.getFirstVideo());
    setPlaylistFinished(false);
  };
  const handleRetry = () => {
    const player = playerRef.current;
    if (player) {
      setIsNetworkError(false);
      player.src(video.source);
      player.load();
    }
  };
  const handleStartNewVideo = () => {
    var _playlist$findByIndex;
    const player = playerRef.current;
    if (!player) {
      return;
    }
    trackVideoProgress(video, player.played());
    const nextVideo = (_playlist$findByIndex = playlist.findByIndex(currentVideoIndex + 1)) !== null && _playlist$findByIndex !== void 0 ? _playlist$findByIndex : video;
    setCurrentVideo(nextVideo);
    setPlaylistFinished(false);
  };
  const playerLoaded = cssLoaded && playerReady;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ErrorOverlay, {
    show: isNetworkError,
    handleClose: handleExit,
    handleRetry: handleRetry,
    isMobile: isMobile,
    "data-sentry-element": "ErrorOverlay",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement(LoadingOverlay, {
    show: !playerLoaded,
    "data-sentry-element": "LoadingOverlay",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement(FinishedPlaylistOverlay, {
    show: playlistFinished && !playlistOpen,
    handleClose: handleExit,
    handleRestart: handlePlaylistRestart,
    handleStartNewVideo: handleStartNewVideo,
    isMobile: isMobile,
    currentVideoIndex: currentVideoIndex,
    playlistLength: playlist.length(),
    "data-sentry-element": "FinishedPlaylistOverlay",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement(StyledVJS, {
    "data-vjs-player": true,
    ref: videoContainer,
    "data-sentry-element": "StyledVJS",
    "data-sentry-source-file": "VideoJS.tsx"
  }, /*#__PURE__*/React.createElement(Onboarding, {
    isMobile: isMobile,
    "data-sentry-element": "Onboarding",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement(Slide, {
    direction: "up",
    in: playlistOpen && !adPlaying,
    mountOnEnter: true,
    unmountOnExit: true,
    "data-sentry-element": "Slide",
    "data-sentry-source-file": "VideoJS.tsx"
  }, /*#__PURE__*/React.createElement(PlaylistOverlay, {
    onClose: () => setPlaylistOpen(false),
    video: video,
    playlist: playlist,
    playerRef: playerRef,
    playerReady: playerReady,
    adPlaying: adPlaying,
    muted: muted,
    forceMuted: forceMuted,
    toggleMute: toggleMute,
    playlistFinished: playlistFinished,
    setPlaylistFinished: setPlaylistFinished,
    currentVideoIndex: currentVideoIndex,
    playlistController: playlistPluginRef.current,
    "data-sentry-element": "PlaylistOverlay",
    "data-sentry-source-file": "VideoJS.tsx"
  })), /*#__PURE__*/React.createElement(HeaderBar, {
    adPlaying: adPlaying,
    playlistOpen: playlistOpen,
    playerRef: playerRef,
    playerReady: playerReady,
    playlist: playlist,
    video: video,
    hideAll: hideAll || !playerLoaded,
    onExitPlayer: handleExit,
    currentVideoIndex: currentVideoIndex,
    "data-sentry-element": "HeaderBar",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement(PlayerOptions, {
    show: !adPlaying && !playlistOpen && !hideAll && playerLoaded,
    onPlaylistToggleClick: () => setPlaylistOpen(true),
    video: video,
    isMobile: isMobile,
    muted: muted,
    forceMuted: forceMuted,
    toggleMute: toggleMute,
    "data-sentry-element": "PlayerOptions",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement(CountdownTimer, {
    remainingTime: remainingTime,
    show: adPlaying,
    "data-sentry-element": "CountdownTimer",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement(CustomControls, {
    playerRef: playerRef,
    playerReady: playerReady,
    videoContainerRef: videoContainer,
    video: video,
    playlist: playlist,
    playlistPlugin: playlistPluginRef.current,
    isMobile: isMobile,
    adPlaying: adPlaying,
    show: !adPlaying && !playlistOpen && playerLoaded,
    setHideAll: setHideAll,
    playlistFinished: playlistFinished,
    currentVideoIndex: currentVideoIndex,
    "data-sentry-element": "CustomControls",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement(FuPaTvLogo, {
    video: video,
    "data-sentry-element": "FuPaTvLogo",
    "data-sentry-source-file": "VideoJS.tsx"
  }), /*#__PURE__*/React.createElement("video", {
    id: "content_video",
    ref: videoRef,
    className: "video-js",
    playsInline: true
  })));
}